import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../store'
import { setShowExceededModal } from '../../store/slices/quotaSlice'

interface QuotaExceededModalProps {
  isOpen?: boolean
  onClose?: () => void
  reason?: string
}

const QuotaExceededModal: React.FC<QuotaExceededModalProps> = ({ 
  isOpen: propIsOpen, 
  onClose: propOnClose,
  reason: propReason 
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showExceededModal, exceededReason, exceededTitle, currentCount, limit, planName } = useAppSelector((state) => state.quota)
  
  const isOpen = propIsOpen ?? showExceededModal
  const reason = propReason ?? exceededReason
  
  const handleClose = () => {
    if (propOnClose) {
      propOnClose()
    } else {
      dispatch(setShowExceededModal(false))
    }
  }
  
  const handleUpgrade = () => {
    handleClose()
    // 先进项目自带套餐页：展示当前套餐状态，卡片内再点升级进入 Casdoor 自建支付
    navigate('/plans')
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg 
              className="w-6 h-6 text-red-600 dark:text-red-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {exceededTitle || '笔记数量已达上限'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {reason || `您已使用 ${currentCount}/${limit} 条笔记`}
            </p>
          </div>
        </div>

        {exceededTitle ? (
          /* 数据库超限等场景：提示空间口径与释放方式（限额随套餐：免费 0.5M/会员 20M） */
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">数据库总空间</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {planName === 'free' ? '0.5M' : '20M'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">约可存储</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {planName === 'free' ? '5 万-10 万汉字' : '200 万-400 万汉字'}
              </span>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              删除不需要的笔记并清空回收站即可释放空间；回收站中的笔记仍占用数据库空间。
            </p>
          </div>
        ) : (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">当前计划</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {planName === 'free' ? '免费版' : planName === 'vip' ? '畅用会员' : '畅存会员'}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">已使用</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {currentCount} 条笔记
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">配额限制</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {limit} 条笔记
            </span>
          </div>

          <div className="mt-3">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-red-500 h-2 rounded-full"
                style={{ width: `${Math.min(100, (currentCount / limit) * 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            稍后再说
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            立即升级
          </button>
        </div>
      </div>
    </div>
  )
}

export default QuotaExceededModal
